import React, { useEffect, useId, useRef, useState } from 'react';
import { useColorMode } from '@docusaurus/theme-common';
import useIsBrowser from '@docusaurus/useIsBrowser';
import clsx from 'clsx';
import styles from './styles.module.css';

const OPTIONS = [
  { value: 'light', label: 'Claro' },
  { value: 'dark', label: 'Escuro' },
  { value: 'system', label: 'Sistema' },
];

function ThemeIcon({ mode }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
  };

  if (mode === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }

  if (mode === 'dark') {
    return (
      <svg {...common}>
        <path d="M20.2 15.9A8.5 8.5 0 0 1 8.1 3.8a8.5 8.5 0 1 0 12.1 12.1Z" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M8 22h8m-4-4v4" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function NavbarColorModeToggle({ className }) {
  const { colorModeChoice, setColorMode } = useColorMode();
  const isBrowser = useIsBrowser();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionsId = useId();
  const choice = isBrowser ? (colorModeChoice ?? 'system') : 'system';
  const activeOption = OPTIONS.find((option) => option.value === choice) ?? OPTIONS[2];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handleOutsidePointer);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointer);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const selectMode = (value) => {
    setColorMode(value === 'system' ? null : value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className={clsx(styles.themeSwitcher, className)}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={`Tema atual: ${activeOption.label}. Alterar tema`}
        title={`Tema: ${activeOption.label}`}
        aria-expanded={open}
        aria-controls={optionsId}
        onClick={() => setOpen((current) => !current)}
        disabled={!isBrowser}>
        <span className={styles.triggerIcon}>
          <ThemeIcon mode={choice} />
        </span>
      </button>
      <div id={optionsId} className={styles.options} role="group" aria-label="Selecionar tema" hidden={!open}>
        {OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={clsx(styles.option, option.value === choice && styles.optionSelected)}
            aria-pressed={option.value === choice}
            onClick={() => selectMode(option.value)}>
            <ThemeIcon mode={option.value} />
            <span>{option.label}</span>
            {option.value === choice && (
              <span className={styles.check}>
                <CheckIcon />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
